import { playLivechatNotificationSound as playLivechatTone } from "./dashboardSounds";

type BrowserWindow = Window & typeof globalThis;

interface LivechatNotificationOptions {
  title?: string;
  body?: string;
  onOpen?: () => void;
  eventKey?: string | null;
  browserWindow?: BrowserWindow;
}

interface LivechatNotificationConstructorOptions extends NotificationOptions {
  renotify?: boolean;
}

const OS_NOTIFICATION_TAG = "di-livechat-notification";

function isBrowserTabInView(browserWindow: BrowserWindow): boolean {
  const browserDocument = browserWindow?.document;
  if (!browserDocument) return true;
  if (browserDocument.visibilityState === "hidden") return false;
  return typeof browserDocument.hasFocus === "function" ? browserDocument.hasFocus() : true;
}

export function getLivechatOsNotificationPermission(
  browserWindow: BrowserWindow = window,
): NotificationPermission | "unsupported" {
  if (typeof browserWindow?.Notification !== "function") return "unsupported";
  return browserWindow.Notification.permission;
}

export async function playLivechatNotificationSound(browserWindow = window) {
  if (
    !isBrowserTabInView(browserWindow) &&
    getLivechatOsNotificationPermission(browserWindow) === "granted"
  ) {
    return false;
  }
  return playLivechatTone(browserWindow);
}

/** Requests OS notification permission directly from a user gesture. */
export async function requestLivechatOsNotificationPermission(
  browserWindow: BrowserWindow = window,
): Promise<NotificationPermission | "unsupported"> {
  if (typeof browserWindow?.Notification !== "function") return "unsupported";
  try {
    return await browserWindow.Notification.requestPermission();
  } catch {
    return browserWindow.Notification.permission;
  }
}

/** Primes the permission prompt for the next pointer or keyboard interaction. */
export function primeLivechatOsNotificationPermission(
  browserWindow: BrowserWindow = window,
): () => void {
  if (getLivechatOsNotificationPermission(browserWindow) !== "default") return () => {};

  const cleanup = () => {
    browserWindow.removeEventListener("pointerdown", request);
    browserWindow.removeEventListener("keydown", request);
  };
  const request = () => {
    cleanup();
    try {
      void browserWindow.Notification.requestPermission();
    } catch {
      // A failed prompt simply leaves OS notifications disabled.
    }
  };
  browserWindow.addEventListener("pointerdown", request);
  browserWindow.addEventListener("keydown", request);
  return cleanup;
}

/** Shows an OS notification only while the dashboard tab is out of view. */
export function showLivechatOsNotification({
  title,
  body,
  onOpen,
  eventKey,
  browserWindow = window,
}: LivechatNotificationOptions = {}): Notification | null {
  if (
    getLivechatOsNotificationPermission(browserWindow) !== "granted" ||
    isBrowserTabInView(browserWindow)
  ) {
    return null;
  }

  try {
    const notificationOptions: LivechatNotificationConstructorOptions = {
      body,
      tag: eventKey ? `${OS_NOTIFICATION_TAG}-${eventKey}` : OS_NOTIFICATION_TAG,
      renotify: true,
    };
    const notification = new browserWindow.Notification(title as string, notificationOptions);
    notification.onclick = () => {
      try {
        browserWindow.focus();
      } catch {
        // Browser focus may be blocked.
      }
      onOpen?.();
      notification.close();
    };
    return notification;
  } catch {
    return null;
  }
}
